import { Box, Chip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCompanyOfficers, getResource } from "../../data/request";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY } from "../colors";

// Bounds how many officers we fan out to (each is a separate API call for
// their full appointment history) and how many other companies we show per
// officer - a serial company-formation director could otherwise have dozens.
const MAX_OFFICERS_TO_CHECK = 20;
const MAX_OTHER_COMPANIES_SHOWN = 5;

type SharedDirector = {
  name: string;
  otherCompanies: { name: string; number: string }[];
};

// Surfaces directors this company shares with other companies - informal
// corporate links that don't show up anywhere in the formal PSC/subsidiary
// register. No dedicated backend for this - it's built entirely from calls
// the app already makes elsewhere (company officers, and each officer's own
// cross-company appointment history).
export const DirectorNetworkPanel = (props: {
  companyNo: string | undefined;
  id?: string;
  onHasData?: (hasData: boolean) => void;
}) => {
  const { companyNo, id, onHasData } = props;
  const [directors, setDirectors] = useState<SharedDirector[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo == undefined) {
        return;
      }
      setLoading(true);

      const officersDoc = await getCompanyOfficers(companyNo);
      const activeOfficers = (officersDoc?.items ?? [])
        .filter((o: any) => o.resigned_on === undefined && o.links?.officer?.appointments)
        .slice(0, MAX_OFFICERS_TO_CHECK);

      const results = await Promise.all(
        activeOfficers.map(async (officer: any): Promise<SharedDirector | null> => {
          try {
            const appointmentsDoc = await getResource(officer.links.officer.appointments);
            const seenNumbers = new Set<string>();
            const otherCompanies = (appointmentsDoc?.items ?? [])
              .filter(
                (a: any) =>
                  a.resigned_on === undefined &&
                  a.appointed_to?.company_number &&
                  a.appointed_to.company_number !== companyNo
              )
              .map((a: any) => ({ name: a.appointed_to.company_name, number: a.appointed_to.company_number }))
              .filter((c: { name: string; number: string }) => {
                if (seenNumbers.has(c.number)) return false;
                seenNumbers.add(c.number);
                return true;
              });

            return otherCompanies.length > 0 ? { name: officer.name, otherCompanies } : null;
          } catch {
            return null;
          }
        })
      );

      const found = results.filter((r): r is SharedDirector => r !== null);
      setDirectors(found);
      setLoading(false);
      onHasData?.(found.length > 0);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNo]);

  // No spinner here - this sits below the Officers table, which already
  // shows its own loading state, and a second independent spinner appearing
  // and disappearing separately would look broken rather than helpful.
  if (loading || directors.length === 0) {
    return null;
  }

  return (
    <Box id={id} style={{ padding: 20, scrollMarginTop: 16 }}>
      <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700 }}>
        Shared Directorships
      </Typography>
      <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 16 }}>
        Officers of this company who are also currently a director or secretary elsewhere.
      </Typography>
      {directors.map((d) => (
        <Box key={d.name} style={{ marginBottom: 16 }}>
          <Typography variant="subtitle2">{d.name}</Typography>
          <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {d.otherCompanies.slice(0, MAX_OTHER_COMPANIES_SHOWN).map((c) => (
              <Chip
                key={c.number}
                label={c.name}
                component={Link}
                to={`/company/${c.number}`}
                clickable
                size="small"
                variant="outlined"
                style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
              />
            ))}
            {d.otherCompanies.length > MAX_OTHER_COMPANIES_SHOWN && (
              <Chip label={`+${d.otherCompanies.length - MAX_OTHER_COMPANIES_SHOWN} more`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DirectorNetworkPanel;
